import axios from "axios";
import Vue from "vue";
import moment from "moment/moment";
import { attachApiErrorReporting } from "@/api/api-error-reporter";

const API_URL =
  process.env.NODE_ENV === "development"
    ? "/api"
    : "https://md-booking.hmc.co.il/ex-doctor/external";
const CancelToken = axios.CancelToken;
var cancel_request = false;

const authHeader = () => `Bearer ${Vue.$cookies.get("hmc_token")}`;

const http = axios.create({
  baseURL: API_URL,
  withCredentials: true
});

/* Response Interceptors */
const interceptResErrors = err => {
  try {
    if (
      err.response &&
      err.response.status === 401 &&
      window.location.pathname !== "/"
    ) {
      // logout and go to login
      localStorage.removeItem("hmc-user");
      window.location = "/";
    }
  } catch (e) {
    if (e.response.status === 401 && window.location.pathname !== "/") {
      localStorage.removeItem("hmc-user");
      window.location = "/";
    }
  }
  return Promise.reject(err);
};
const interceptResponse = res => {
  try {
    return Promise.resolve(res);
  } catch (e) {
    if (e.response.status === 401 && window.location.pathname !== "/") {
      localStorage.removeItem("hmc-user");
      window.location = "/";
    }
    return Promise.resolve(res);
  }
};
http.interceptors.response.use(interceptResponse, interceptResErrors);

/* Request Interceptors */
const interceptReqErrors = err => Promise.reject(err);
const interceptRequest = config => {
  return config;
};
http.interceptors.request.use(interceptRequest, interceptReqErrors);

/* Telemetry: fire-and-forget reporting for any failed response. */
attachApiErrorReporting(http);

export default class API {
  API = http;
  getToken() {
    return new Promise((resolve, reject) => {
      this.API.get("/login").then(({ data }) => {
        resolve(data);
      });
    });
  }
  login(username, password) {
    return new Promise((resolve, reject) => {
      this.API.post("/login", {
        username: username,
        password: password
      })
        .then(res => {
          if (res.status == 200) {
            resolve(res.data);
          }
          resolve(false);
        })
        .catch(e => reject(e.response.data));
    });
  }
  sendSMS(user_id) {
    this.API.post("/send_sms", {
      user_id: user_id
    });
  }
  getConnectedCalendars(user_id) {
    return new Promise((resolve, reject) => {
      this.API.post(
        "/connected_calendars",
        {
          user_id: user_id
        },
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => {
          if (res.status == 200) {
            resolve(res.data);
          }
          resolve(false);
        })
        .catch(e => reject(e.response.data));
    });
  }
  validateSMS(sms, user_id) {
    return new Promise((resolve, reject) => {
      this.API({
        method: "POST",
        url: "/login",
        data: {
          user_id: user_id,
          two_factor_token: sms
        }
      })
        .then(res => {
          if (res.status == 200) {
            resolve(res.data);
          }
          resolve(false);
        })
        .catch(e => reject(e.response.data));
    });
  }
  /*
  not in use
   */
  getBlockTypes(doctor_id, block_type) {
    return new Promise((resolve, reject) => {
      this.API({
        method: "post",
        url: "/get-block-type-dates",
        data: {
          doctor_id: doctor_id,
          block_type: block_type
        },
        headers: {
          Authorization: authHeader()
        }
      })
        .then(res => resolve(res))
        .catch(reject);
    });
  }
  /*
  not in use
   */
  getBlockTypeDates(doctor_id, block_type) {
    return new Promise((resolve, reject) => {
      this.API.post(
        "/get_block_type_dates",
        {
          doctor_id: doctor_id,
          block_type: block_type
        },
        {
          headers: {
            Authorization: authHeader()
          }
        }
      ).then(res => {
        resolve(res.data);
      });
    });
  }
  getFullCalendarEvents(
    user_id = false,
    doctor_id,
    event_type = "appointment",
    startDate,
    endDate,
    block_id = false
  ) {
    return new Promise((resolve, reject) => {
      let formData = {};
      formData["user_id"] = user_id;
      formData["doctor_id"] = doctor_id;
      formData["event_type"] = event_type;
      formData["start"] = startDate;
      formData["end"] = endDate;
      formData["view_type"] = "list";
      if (block_id) {
        formData["block_id"] = block_id;
        formData["single_block_view"] = "yes";
      }

      //formData.append("event_type", "surgery");
      this.API({
        method: "post",
        url: "/fullcalendar_events_ajax",
        data: formData,
        headers: {
          Authorization: authHeader()
        },
        cancelToken: new CancelToken(function executor(c) {
          cancel_request = c;
        })
      }).then(res => {
        resolve(res.data);
      });
    });
  }
  getAppointmentInfo(appointment_id) {
    return new Promise((resolve, reject) => {
      this.API.post(
        "/appointment_info",
        {
          appointment_id: appointment_id
        },
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }
  getAppointmentDoc(url) {
    return new Promise((resolve, reject) => {
      this.API.post(
        url,
        {},
        {
          headers: {
            Authorization: authHeader()
          }
        }
      ).then(res => {
        resolve(res.data);
      });
    });
  }
  getDocApp(fileUrl) {
    return new Promise((resolve, reject) => {
      this.API.post(
        fileUrl,
        {
          temp_url: "temp_url"
        },
        {
          headers: {
            Authorization: authHeader()
          }
        }
      ).then(res => {
        resolve(res.data);
      });
    });
  }
  getBlockInfo(block_id) {
    return new Promise((resolve, reject) => {
      this.API.post(
        "/get_block_info",
        {
          block_id: block_id
        },
        {
          headers: {
            Authorization: authHeader()
          }
        }
      ).then(res => {
        resolve(res.data);
      });
    });
  }

  getSurgeryInfo(surgery_id) {
    return new Promise((resolve, reject) => {
      this.API.post(
        "/surgery_info",
        {
          surgery_id: surgery_id
        },
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => {
          if (res.status == 200) {
            resolve(res.data);
          }
          resolve(false);
        })
        .catch(e => reject(e.response.data));
    });
  }
  /*
  not in use
   */
  getSurgeries(doctor_license) {
    return new Promise((resolve, reject) => {
      this.API.post(
        "/surgeries",
        {
          surgery_id: 67031
        },
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => {
          if (res.status == 200) {
            resolve(res.data);
          }
          resolve(false);
        })
        .catch(e => reject(e.response.data));
    });
  }
  /*
  not in use
   */
  saveAppointment(appointment) {
    return new Promise((resolve, reject) => {
      this.API.post(
        "/save-appointment-agenda",
        {
          appointments: appointment
        },
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => {
          resolve(res.data);
        })
        .catch(e => reject(e.response.data));
    });
  }
  /*
  not in use
   */
  appointmentApprove(value, appointment_id) {
    return new Promise((resolve, reject) => {
      this.API.post(
        "/appointment-xeditable",
        {
          name: "arrival_approval",
          value: value,
          pk: appointment_id
        },
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => {
          resolve(res.data);
        })
        .catch(e => reject(e.response.data));
    });
  }
  searchAppointments(
    doctorId,
    query,
    type,
    appointmentType = "surgery-appointment-gastro-catheterization"
  ) {
    return new Promise((resolve, reject) => {
      /*this.API.post("/search_id", {
        token: TOKEN,
        doctor_id: doctor_id,
        id: id
      }).then(res => {
        //console.log(res);
        resolve(res.data);
      });*/
      // `/appointment_table_ajax?appointment_type=search_all&${type}=` + query,
      this.API({
        baseURL: API_URL,
        method: "POST",
        url: `/arrival_table_ajax?doctor_id=${doctorId}&type=${appointmentType}&${type}=${query}`,
        headers: {
          Authorization: authHeader()
        },
        cancelToken: new CancelToken(function executor(c) {
          cancel_request = c;
        })
      })
        .then(res => {
          resolve(res.data);
        })
        .catch(e => reject(e.response.data));
    });
  }
  cancelAjaxRequest() {
    if (typeof cancel_request == "function") {
      cancel_request();
    }
  }
  /**
   * cancel appointment
   * @param appoitment_id
   * @returns {Promise<unknown>}
   */
  cancelAppointment(appoitment_id) {
    return new Promise((resolve, reject) => {
      this.API.post(
        "/delete_appointment",
        {
          appointment_id: appoitment_id
        },
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => resolve(res))
        .catch(e => reject(e.response.data));
    });
  }
  getBlockTypes() {
    return new Promise((resolve, reject) => {
      this.API.get("/get_block_types", {
        headers: {
          Authorization: authHeader()
        }
      }).then(res => {
        resolve(res.data);
      });
    });
  }
  /*
  not in use
   */
  checkPassport(id, license) {
    return new Promise((resolve, reject) => {
      this.API.post(
        "passport-cheker",
        {
          license: license,
          passport: id
        },
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => {
          resolve(res.data);
        })
        .catch(e => reject(e.response.data));
    });
  }
  getTypehead(
    type,
    cat = null,
    insurance_code = null,
    doctor = null,
    surgery_name = null,
    activity_code = null
  ) {
    /*
    Types :
    material_sensitivity
    medication_sensitivity
    current_diagnosis
    past_diagnosis
    past_surgery
    current_surgery_category תחום
    current_surgery שם קוד ניתוח
    current_procedure שם קוד פרוצדורה
    insurance_surgery_category
    insurance_surgery
    rooms
    payment_methods,
    form_subjects,
    insuarnce_types
     */
    return new Promise((resolve, reject) => {
      let obj = { type: type };
      if (cat) obj.cat = cat;
      if (insurance_code) obj.insurance_code = insurance_code;
      if (doctor) obj.doctor = doctor;
      if (surgery_name) obj.surgery_name = surgery_name;
      if (activity_code) obj.activity_code = activity_code;
      this.API.post("typehead", obj, {
        headers: {
          Authorization: authHeader()
        }
      })
        .then(res => {
          if (res.data) {
            resolve(res.data);
          }
        })
        .catch(e => reject(e.response.data));
    });
  }
  getAutocompleteSurgCode(type, code) {
    return new Promise((resolve, reject) => {
      this.API.post(
        `typehead?type=${type}&activity_code=${code}`,
        {},
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => {
          if (res.data) {
            resolve(res.data);
          }
        })
        .catch(e => reject(e.response.data));
    });
  }
  getDoctorHistory(doctorId) {
    return new Promise((resolve, reject) => {
      this.API.post(
        `typehead?type=doctor_history&doctor=${doctorId}`,
        {},
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => {
          if (res.data) {
            resolve(res.data);
          }
        })
        .catch(e => reject(e.response.data));
    });
  }
  getImplantsBySurgery(code) {
    if (!code) return;
    return new Promise((resolve, reject) => {
      this.API.post(
        `get_available_implants?activity_code=${code}`,
        {},
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => {
          if (res.data) {
            resolve(res.data);
          }
        })
        .catch(e => reject(e.response.data));
    });
  }
  // getTypeheadGET(type, cat = null, insurance_code = null) {
  //   return new Promise((resolve, reject) => {
  //     let obj = { type: type };
  //     if (cat) obj.cat = cat;
  //     if (insurance_code) obj.insurance_code = insurance_code;
  //     this.API.post(`typehead?type=${type}&insurance_code=${insurance_code}`)
  //       .then(res => {
  //         if (res.data) {
  //           resolve(res.data);
  //         }
  //       })
  //       .catch(e => {
  //         reject(e.response.data);
  //       });
  //   });
  // }

  /**
   * validate passport and return user by passport
   * @param passport
   * @param insurance
   * @returns {Promise<unknown>}
   */
  validatePassport(passport, insurance) {
    return new Promise((resolve, reject) => {
      this.API.post(
        "passport-checker",
        {
          passport: passport,
          license: 11115
          //insurance: insurance
        },
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => {
          if (res.status == 200) {
            resolve(res.data);
          }
        })
        .catch(e => reject(e.response.data));
    });
  }

  /**
   * validate connected user
   * @returns {Promise<unknown>}
   */
  getUserInfo() {
    return new Promise((resolve, reject) => {
      this.API.post(
        "/user_info",
        {},
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => {
          if (res.status == 200) {
            resolve(res.data);
          }
          resolve(false);
        })
        .catch(e => reject(e.response.data));
    });
  }
  getNonScheduleEvents(type, doctor_id) {
    return new Promise((resolve, reject) => {
      this.API.post(
        "/non-scheduled-events",
        {
          type: type,
          doctor_id: doctor_id
        },
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => {
          if (res.status == 200) {
            resolve(res.data);
          }
          resolve(false);
        })
        .catch(e => reject(e.response.data));
    });
  }
  getScheduleTable(doctor_id, from_date = false, to_date = false) {
    return new Promise((resolve, reject) => {
      let args = {
        doctor_id: doctor_id,
        show_private: "yes",
        full_busy_block: "yes"
      };
      if (from_date) {
        args.from_date = from_date; //YYYY-MM-DD
      }
      if (to_date) {
        args.to_date = to_date; //YYYY-MM-DD
      }

      this.API.post("/get-available-time-slots", args, {
        headers: {
          Authorization: authHeader()
        }
      })
        .then(res => {
          if (res.status == 200) {
            resolve(res.data);
          }
          resolve(false);
        })
        .catch(e => reject(e.response.data));
    });
  }
  getScheduleTableForSurgery(doctor_id, surgery_id) {
    return new Promise((resolve, reject) => {
      this.API.post(
        "/get-available-time-slots",
        {
          surgery_id: surgery_id,
          // user_id: doctor_id,
          type: "surgery_catheterization_gastro_ivf"
        },
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => {
          if (res.status == 200) {
            resolve(res.data);
          }
          resolve(false);
        })
        .catch(e => reject(e.response.data));
    });
  }
  setSurgeryDateTime(suregery_id, block_id, start, end) {
    return new Promise((resolve, reject) => {
      let data = {
        title: "ניתוח",
        id: suregery_id,
        over_block_id: block_id,
        start: start,
        end: end
      };
      this.API.post(
        "/set_surgery_datetime",
        {
          surgery: data
        },
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => {
          if (res.status == 200) {
            resolve(res.data);
          }
          resolve(false);
        })
        .catch(e => reject(e.response.data));
    });
  }

  appointmentLock(appointment) {
    return new Promise((resolve, reject) => {
      if (typeof appointment != "object") {
        resolve(false);
      }
      this.API.post("/lock_appointment", appointment, {
        headers: {
          Authorization: authHeader()
        }
      })
        .then(res => {
          if (res.status == 200) {
            resolve(res.data);
          }
          resolve(false);
        })
        .catch(e => reject(e.response.data));
    });
  }
  appointmentAbortLock(appointment_id) {
    return new Promise((resolve, reject) => {
      this.API.post(
        "/lock_appointment",
        {
          appointment: "abort",
          appointment_id: appointment_id
        },
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => {
          if (res.status == 200) {
            resolve(res.data);
          }
          resolve(false);
        })
        .catch(e => reject(e.response.data));
    });
  }
  scheduleAppointment(appointment, action = "schedule") {
    return new Promise((resolve, reject) => {
      if (typeof appointment != "object") resolve(false);
      const req = {
        appointment: action,
        block_id: appointment.block_id,
        datetime: appointment.datetime,
        appointment_duration_min: appointment.appointment_duration_min,
        appointment_id: appointment.appointment_id,
        first_name: appointment.first_name,
        last_name: appointment.last_name,
        passport: appointment.passport,
        phone: appointment.phone,
        lang: appointment.lang,
        gender: appointment.gender,
        second_phone: appointment.second_phone,
        email: appointment.email,
        birth_date: appointment.birth_date,
        notes: appointment.notes,
        general_notes: appointment.general_notes,
        appointment_type: appointment.appointment_type,
        insurance: appointment.insurance,
        cost_patient: appointment.cost_patient,
        payment_method: appointment.payment_method,
        arrival_approval: appointment.arrival_status
      };
      if (appointment.passport_type) {
        req.passport_type = appointment.passport_type;
      }
      if (appointment.anesthesia) {
        req.anesthesia = appointment.anesthesia;
      }
      this.API.post("/schedule_appointment", req, {
        headers: {
          Authorization: authHeader()
        }
      })
        .then(res => {
          if (res.status == 200) {
            resolve(res.data);
          }
          resolve(false);
        })
        .catch(e => reject(e.response.data));
    });
  }
  statusArrival(appointment_id, newValue) {
    return new Promise((resolve, reject) => {
      const value = newValue ? "arrived" : "no_show";
      this.API.post(
        "/set_appointment_field",
        {
          pk: appointment_id,
          name: "status",
          value
        },
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => {
          resolve(res.data);
        })
        .catch(e => {
          console.log(e);
          reject(e.response.data);
        });
    });
  }
  approveArrival(appointment, action = "update") {
    return new Promise((resolve, reject) => {
      if (typeof appointment != "object") resolve(false);

      let email = appointment.email;
      if (email === null) email = "";

      const start = moment(appointment.start_datetime);
      const end = moment(appointment.end_datetime);
      const duration = moment.duration(end.diff(start, "minutes"));

      const req = {
        appointment: action,
        block_id: appointment.surgery_block_id,
        datetime: appointment.start_datetime,
        // These ARE minutes! Not miliseconds.
        appointment_duration_min: duration._milliseconds,
        appointment_id: appointment.id,
        first_name: appointment.firstName,
        last_name: appointment.lastName,
        passport: appointment.passport,
        phone: appointment.phone,
        lang: appointment.language,
        second_phone: appointment.second_phone,
        email: email,
        birth_date: appointment.birthDate,
        notes: appointment.notes,
        general_notes: appointment.arrival_notes,
        appointment_type: appointment.type,
        insurance: appointment.insurance,
        cost_patient: appointment.fee,
        payment_method: appointment.payment_method,
        arrival_approval: appointment.arrival_status
      };
      if (appointment.passport_type) {
        req.passport_type = appointment.passport_type;
      }
      this.API.post("/schedule_appointment", req, {
        headers: {
          Authorization: authHeader()
        }
      })
        .then(res => {
          if (res.status == 200) resolve(res.data);
          resolve(false);
        })
        .catch(e => reject(e.response.data));
    });
  }
  uploadFile(
    appointment_id,
    status = "new",
    document_id = null,
    name,
    files,
    isSurg = false,
    type = "medicaldocuments-patient"
  ) {
    return new Promise((resolve, reject) => {
      const obj_id = isSurg
        ? "surgery_" + appointment_id
        : "appointment_" + appointment_id;
      let data = {
        obj_id,
        type,
        status: status,
        document_id: document_id,
        name: name,
        files: [{ base64: files }]
      };
      this.API.post(
        "/upload_external_files",
        {
          data_json: JSON.stringify(data)
        },
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => {
          if (res.status == 200) {
            resolve(res.data);
          }
          resolve(false);
        })
        .catch(e => reject(e.response.data));
    });
  }
  reScheduleAppointment(appointment_id, block_id, start_time) {
    return new Promise((resolve, reject) => {
      let appoitments_changed =
        localStorage.getItem("hmc-app-changed-time") || [];
      try {
        appoitments_changed = JSON.parse(appoitments_changed);
      } catch (e) {
        console.log(e);
      }
      if (appoitments_changed.indexOf(appointment_id) < 0) {
        appoitments_changed.push(appointment_id);
        localStorage.setItem(
          "hmc-app-changed-time",
          JSON.stringify(appoitments_changed)
        );
      }
      this.API.post(
        "/reschedule_appointment",
        {
          appointment_id: appointment_id,
          new_block_id: block_id,
          new_start_time: start_time
        },
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => {
          if (res.status == 200) {
            resolve(res.data);
          }
          resolve(false);
        })
        .catch(e => reject(e.response.data));
    });
  }
  scheduleSuregery(surgery) {
    return new Promise((resolve, reject) => {
      if (typeof surgery != "object") {
        resolve(false);
      }
      this.API.post("/schedule_surgery", surgery, {
        headers: {
          Authorization: authHeader()
        }
      })
        .then(res => {
          if (res.status == 200) {
            resolve(res.data);
          }
          resolve(false);
        })
        .catch(e => reject(e.response.data));
    });
  }
  scheduleTempSurgery(surgery) {
    return new Promise((resolve, reject) => {
      if (!surgery) return resolve(false);
      surgery = {
        ...surgery,
        temp_surgery: "yes",
        surgery_form: "with_orders",
        form_type: "modal",
        json: "ok"
      };

      this.API.post("/schedule_surgery", surgery, {
        headers: {
          Authorization: authHeader()
        }
      })
        .then(res => {
          if (res.status == 200 && res.data?.surgery_id) {
            sessionStorage.setItem("temp_surgery", res.data.surgery_id);
            resolve(res.data.surgery_id);
          } else {
            resolve(false);
          }
        })
        .catch(e => reject(e.response?.data || e));
    });
  }

  savePatientRequest(payload) {
    return this.API.post("/doctor-patient-request", payload, {
      headers: {
        Authorization: authHeader()
      }
    }).then(res => res.data);
  }

  scheduleSurgeryOrders(orders) {
    return new Promise((resolve, reject) => {
      this.API.post("/save_surgery_orders", orders, {
        headers: {
          Authorization: authHeader()
        }
      })
        .then(res => {
          if (res.status == 200) {
            resolve(res.data);
          }
          resolve(false);
        })
        .catch(e => reject(e.response.data));
    });
  }
  getSurgeryOrders(surgery_id) {
    return new Promise((resolve, reject) => {
      this.API.post(
        "/get_event_orders",
        {
          surgery_id
        },
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => {
          if (res.status == 200) {
            resolve(res.data);
          }
          resolve(false);
        })
        .catch(e => reject(e.response.data));
    });
  }
  /*
  not in use
   */
  createBlock(block) {
    return new Promise((resolve, reject) => {
      if (typeof surgery != "object") {
        resolve(false);
      }
      this.API.post("/save_new_block", surgery, {
        headers: {
          Authorization: authHeader()
        }
      })
        .then(res => {
          if (res.status == 200) {
            resolve(res.data);
          }
          resolve(false);
        })
        .catch(e => reject(e.response.data));
    });
  }
  getDoctorBlockTypes(user_id, type) {
    return new Promise((resolve, reject) => {
      this.API.post(
        "/get-doctor-block-types",
        {
          user_id: user_id,
          types: type
        },
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => {
          if (res.status == 200) {
            resolve(res.data);
          }
          resolve(false);
        })
        .catch(e => reject(e.response.data));
    });
  }
  getBlockMaps(user_id, doctor_id, from, to) {
    return new Promise((resolve, reject) => {
      this.API.post(
        "/block_map",
        {
          user_id: user_id,
          doctor_id: doctor_id,
          from_date: from,
          to_date: to
        },
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => {
          if (res.status == 200) {
            resolve(res.data);
          }
          resolve(false);
        })
        .catch(e => reject(e.response.data));
    });
  }
  saveNewBlock(block) {
    return new Promise((resolve, reject) => {
      this.API.post(
        "/save_new_block",
        {
          block
        },
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => {
          if (res.status == 200) {
            resolve(res.data);
          }
          resolve(false);
        })
        .catch(e => reject(e.response.data));
    });
  }
  duplicateBlock(block_id, dates_array) {
    return new Promise((resolve, reject) => {
      this.API.post(
        "/duplicate-blocks",
        {
          parent_block_id: block_id,
          dup_blocks_array: dates_array
        },
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => {
          if (res.status == 200) {
            resolve(res.data);
          }
          resolve(false);
        })
        .catch(e => reject(e.response.data));
    });
  }
  setAppointementsDatetime(appointements) {
    return new Promise((resolve, reject) => {
      this.API.post(
        "/set_appointments_datetime",
        {
          appointments: appointements
        },
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => {
          if (res.status == 200) {
            resolve(res.data);
          }
          resolve(false);
        })
        .catch(e => reject(e.response.data));
    });
  }
  getPendingAppointments(doctor_id, types = "appointment") {
    return new Promise((resolve, reject) => {
      this.API.post(
        "/get-pending-notifications",
        {
          doctor_id: doctor_id,
          types: types
        },
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => {
          if (res.status == 200) {
            resolve(res.data);
          }
          resolve(false);
        })
        .catch(e => reject(e.response.data));
    });
  }
  sendPendingAppointmentsNotifications(events, send_sms = false) {
    let data = { event_ids: events };
    if (send_sms) {
      data.action = "send_notification";
    }
    return new Promise((resolve, reject) => {
      this.API.post("/send-notifications-to-pending-events", data, {
        headers: {
          Authorization: authHeader()
        }
      })
        .then(res => {
          if (res.status == 200) {
            resolve(res.data);
          }
          resolve(false);
        })
        .catch(e => reject(e.response.data));
    });
  }
  logout() {
    return new Promise((resolve, reject) => {
      this.API.post(
        "/logout",
        {},
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => {
          if (res.status == 200) {
            resolve(res.data);
          }
          resolve(false);
        })
        .catch(e => reject(e.response.data));
    });
  }
  editBlockStartTime(block_id, new_start_time) {
    return new Promise((resolve, reject) => {
      this.API.post(
        "/set_block_start_time",
        {
          block_id,
          new_start_time
        },
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => {
          if (res.status == 200) {
            resolve(res.data);
          }
          resolve(false);
        })
        .catch(e => reject(e.response.data));
    });
  }
  editBlocks(blocks) {
    return new Promise((resolve, reject) => {
      this.API.post(
        "/edit_block",
        {
          blocks: blocks
        },
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => {
          if (res.status == 200) {
            resolve(res.data);
          }
          resolve(false);
        })
        .catch(e => reject(e.response.data));
    });
  }
  delete_block(block_id) {
    return new Promise((resolve, reject) => {
      this.API.post(
        "/delete_block",
        {
          blocks: block_id
        },
        {
          headers: {
            Authorization: authHeader()
          }
        }
      ).then(res => resolve(res));
    });
  }
  change_block_status(block_id, status = "private") {
    return new Promise((resolve, reject) => {
      this.API.post(
        "/update_block_data",
        {
          name: "status",
          value: status,
          blocks_id: block_id
        },
        {
          headers: {
            Authorization: authHeader()
          }
        }
      ).then(res => resolve(res));
    });
  }
  getAllDoctorBlockType(user_id) {
    return new Promise((resolve, reject) => {
      this.API.post(
        "/get-doctor-block-types",
        {
          user_id: user_id,
          types: "surgery_catheterization_gastro_ivf"
        },
        {
          headers: {
            Authorization: authHeader()
          }
        }
      ).then(res => resolve(res));
    });
  }
  sendForm(sendForm) {
    return new Promise((resolve, reject) => {
      this.API.post("/doctor-support-form", sendForm, {
        headers: {
          Authorization: authHeader()
        }
      })
        .then(res => resolve(res))
        .catch(e => reject(e.response.data));
    });
  }
  getRoomsByDoctor(doctor_id) {
    return new Promise((resolve, reject) => {
      this.API.post(
        "/doctor-rooms",
        {
          doctor_id: doctor_id
        },
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => resolve(res))
        .catch(e => reject(e.response.data));
    });
  }
  userSubscription(token) {
    if (navigator.userAgent === "hmcAppUserAgent") {
      return Promise.reject("App request for user subscription not allowed.");
    }
    return new Promise((resolve, reject) => {
      this.API.post(
        "/user-subscription",
        {
          google_subscription_token: token
        },
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => resolve(res))
        .catch(e => reject(e.response.data));
    });
  }
  getQuestions(token) {
    return new Promise((resolve, reject) => {
      this.API.post(
        "/user-subscription",
        {
          google_subscription_toke: token
        },
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => resolve(res))
        .catch(e => reject(e.response.data));
    });
  }
  getAppSummeryQuestions(token) {
    return new Promise((resolve, reject) => {
      this.API.post(
        "/get-process-flow?process_type=doctor_visit_v2",
        {},
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => resolve(res))
        .catch(e => reject(e.response.data));
    });
  }
  sendAppSummery(appointment_id, answers_json) {
    return new Promise((resolve, reject) => {
      this.API.post(
        "/save-process-answers",
        {
          obj_id: "appointment_" + appointment_id,
          answers_json,
          process_type: "doctor_visit_v2",
          next_step: "exit"
        },
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => resolve(res))
        .catch(e => reject(e.response.data));
    });
  }
  getSurgeryDocuments(surgId) {
    return new Promise((resolve, reject) => {
      this.API.get(
        `/get-patient-files?type=surgery_${surgId}&uploaded_by_type=all`,
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => resolve(res))
        .catch(e => reject(e.response.data));
    });
  }
  deleteSurgery(surgeryId, type = false) {
    return new Promise((resolve, reject) => {
      // Send delete_type=calendar for delete from calender only
      let req = {
        surgery_id: surgeryId
      };
      if (type) req.delete_type = "calendar";

      this.API.post(`/delete_surgery`, req, {
        headers: {
          Authorization: authHeader()
        }
      })
        .then(res => resolve(res))
        .catch(e => reject(e.response.data));
    });
  }
  /**
   * Send event SMS to patient, either for appointment ir surgery
   * @param {number} id event id
   * @param {string} type values: "appointment" or "surgery"
   * @param {string} content text to be sent
   * @returns {Promise}
   */
  sendSmsToPatient(id, type, content) {
    return new Promise((resolve, reject) => {
      const msgContent = content;
      const sms = "sms";

      let req = {
        message_content: msgContent,
        sms_checkbox: sms
      };

      if (type === "appointment") req.appointment_id = id;
      else req.surgery_id = id;

      this.API.post(`/message_to_patients`, req, {
        headers: {
          Authorization: authHeader()
        }
      })
        .then(res => resolve(res))
        .catch(e => reject(e.response.data));
    });
  }
  getTableDocsList({ doctorId, fromDate, toDate }) {
    return new Promise((resolve, reject) => {
      this.API.get(
        `/get-documents?doctor_id=${doctorId}&from_date=${fromDate}&to_date=${toDate}`,
        {
          headers: {
            Authorization: authHeader()
          }
        }
      )
        .then(res => resolve(res))
        .catch(e => reject(e.response.data));
    });
  }
  updatePathologyAvailability(id, status, license, doctor_id) {
    const value = "resend";
    // For now we are using resend status only
    // const value = status ? "offline" : "resend";
    return new Promise((resolve, reject) => {
      this.API.post(
        `/document-xeditable?name=status&value=${value}&pk=${id}&license=${license}&doctor_id=${doctor_id}`,
        {},
        {
          headers: {
            Authorization: authHeader()
          }
        }
      ).then(res => {
        resolve(res.data);
      });
    });
  }
  getIVFList(doctorId, query, queryType, form, to) {
    let url = `/arrival_table_ajax?doctor_id=${doctorId}&type=appointment-with_no_date`;
    if (queryType && query) {
      url += `&${queryType}=${query}`;
    }

    if (form && to) {
      url += `&from=${form}&to=${to}`;
    }

    return new Promise((resolve, reject) => {
      this.API({
        baseURL: API_URL,
        method: "POST",
        url: url,
        headers: {
          Authorization: authHeader()
        },
        cancelToken: new CancelToken(function executor(c) {
          cancel_request = c;
        })
      })
        .then(res => {
          resolve(res.data);
        })
        .catch(e => reject(e.response.data));
    });
  }
}
